import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UvalueCalculator.css';
import { useData } from './AuthContext';

const EmailQuote = ({ formData, productUrl, newQuote, product, result }) => {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [calcName, setCalcName] = useState('');
    const [showPrompt, setShowPrompt] = useState(false);
    const [reCap, setReCap] = useState(false);

    const { saveCalculation } = useData();

    // ✅ Load reCAPTCHA check
    useEffect(() => {
        if (window.grecaptcha) {
            window.grecaptcha.ready(() => {
                window.grecaptcha
                    .execute("6Lci_S8rAAAAAGfq9UaTD-kCGoYMYg-2mPTOMoae", { action: "submit" })
                    .then((token) => {
                        fetch("https://uvalue-calculator.nimbus.sugarprojects.com/wp-json/recaptcha/v1/verify", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ token }),
                        })
                            .then((res) => res.json())
                            .then((data) => {
                                setReCap(data.success && data.score >= 0.5);
                            });
                    });
            });
        }
    }, []);

    // ✅ Email PDF
    const handleEmailSubmit = async () => {
        setIsSubmitting(true);
        formData.MakePDF = 1;

        try {
            const apiUrl =
                process.env.NODE_ENV === 'production'
                    ? 'https://mannok.jpatl.com/api/getuvalue'
                    : '/api/getuvalue';

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            const data = await response.json();
            const { uvalue, downloadUrl } = data;

            await axios.post('https://uvalue-calculator.nimbus.sugarprojects.com/wp-json/email/v1/send-quote', {
                email,
                uValue: uvalue,
                url: downloadUrl,
                productUrl,
                data,
                formData
            });

            formData.MakePDF = 0;

            const timestamp = new Date().toLocaleString();
            const uniqueId = Date.now();
            const responsiveUrl = productUrl.replace(/.*\.com/, '');

            const temp = {
                id: uniqueId,
                timestamp,
                url: responsiveUrl,
            };

            newQuote(temp);
            setMessage('U-Value has been sent successfully!');
        } catch (error) {
            console.error('Error:', error);
            setMessage('There was an error sending the U-Value. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ✅ Download PDF in a new tab
    const handleDownloadPDF = async () => {
        try {
            setIsSubmitting(true);
            formData.MakePDF = 1;

            const apiUrl =
                process.env.NODE_ENV === 'production'
                    ? 'https://mannok.jpatl.com/api/getuvalue'
                    : '/api/getuvalue';

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            const data = await response.json();
            const { downloadUrl } = data;

            if (downloadUrl) {
                // ✅ Open the PDF in a new browser tab
                window.open(downloadUrl, '_blank');
            } else {
                alert('Failed to generate PDF. Please try again.');
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF.');
        } finally {
            formData.MakePDF = 0;
            setIsSubmitting(false);
        }
    };

    // ✅ Save Calculation
    const saveCalc = () => {
        saveCalculation(calcName, product, formData, result);

        if (!calcName) return;

        const timestamp = new Date().toLocaleString();
        const uniqueId = Date.now();
        const responsiveUrl = productUrl.replace(/.*\.com/, '');

        const temp = {
            id: uniqueId,
            name: calcName,
            timestamp,
            url: responsiveUrl,
        };

        newQuote(temp);
        setIsSaved(true);
        setShowPrompt(false);

        setTimeout(() => {
            setIsSaved(false);
        }, 2000);
    };

    return (
        <div className='uk-margin-top'>
            {/* ✅ Buttons side-by-side */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                    marginBottom: "30px",
                    flexWrap: "wrap",
                }}
            >
                <button
                    className="save-button"
                    style={{
                        backgroundColor: isSaved ? "#4CAF50" : "#6294AA",
                    }}
                    onClick={() => setShowPrompt(true)}
                    disabled={isSubmitting}
                >
                    {isSaved ? "Calculation Saved!" : "Save Calculation"}
                </button>

                <button
                    className="save-button"
                    style={{ backgroundColor: "#6294AA" }}
                    onClick={handleDownloadPDF}
                    disabled={isSubmitting}
                >
                    Download PDF
                </button>
            </div>

            {showPrompt && (
                <div className="custom-prompt">
                    <div className="prompt-content">
                        <h3>Enter a name for your calculation:</h3>
                        <input
                            type="text"
                            value={calcName}
                            onChange={(e) => setCalcName(e.target.value)}
                            className="prompt-input"
                        />
                        <div className="button-group">
                            <button className="save-btn" onClick={saveCalc}>Save</button>
                            <button className="cancel-btn" onClick={() => setShowPrompt(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            <label className="uk-form-label uk-display-block">U-Value PDF</label>
            <p className='uk-text-medium'>
                To receive a PDF of your U-Value calculation, enter your email address here:
            </p>

            {reCap && (
                <div className="uk-margin-top uk-flex uk-width-1-1">
                    <input
                        type="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mobile mobile-email-input"
                        disabled={isSubmitting}
                        style={{ width: '75%', paddingBottom: "0px", paddingTop: "0px" }}
                    />
                    <button
                        className="mobile send-button"
                        type="button"
                        onClick={handleEmailSubmit}
                        disabled={isSubmitting || !email}
                    >
                        <img src="./Send.svg" alt="Send" />
                    </button>
                </div>
            )}

            {message && <p>{message}</p>}
        </div>
    );
};

export default EmailQuote;
